import {
	useCallback,
	useEffect,
	useLayoutEffect,
	useRef,
	useState,
} from 'react';

import { PostDownloadButton } from '@/components/artifact/PostDownloadButton';
import { BlobImage } from '@/components/blob/BlobImage';
import { cn } from '@/utils/cn';

import type { InstagramPost } from '../../../../bridge/types/artifact.ts';

interface InstagramPostPreviewProps {
	posts: InstagramPost[];
	selectedPostIndex: number;
	contentPanel: React.ReactNode;
	className?: string;
	onSelectPost?: (index: number) => void;
}

export const InstagramPostPreview = ({
	posts,
	selectedPostIndex,
	contentPanel,
	className,
	onSelectPost,
}: InstagramPostPreviewProps) => {
	const selectedPost = posts[selectedPostIndex];
	const slides = selectedPost?.content.image_urls ?? [];
	const isCarousel = slides.length > 1;

	const [currentSlide, setCurrentSlide] = useState(0);
	const [isViewportHovered, setIsViewportHovered] = useState(false);
	const [isDownloadMenuOpen, setIsDownloadMenuOpen] = useState(false);
	const viewportRef = useRef<HTMLDivElement>(null);
	const slideRefs = useRef<(HTMLDivElement | null)[]>([]);
	const hasPositionedRef = useRef(false);

	useEffect(() => {
		setCurrentSlide(0);
	}, [selectedPostIndex]);

	const safeSlide = Math.min(currentSlide, Math.max(slides.length - 1, 0));

	const goPrev = useCallback(
		() => setCurrentSlide((i) => Math.max(0, i - 1)),
		[],
	);
	const goNext = useCallback(
		() =>
			setCurrentSlide((i) => Math.min(Math.max(slides.length - 1, 0), i + 1)),
		[slides.length],
	);

	useEffect(() => {
		if (!isCarousel || !isViewportHovered) return;
		const handler = (event: KeyboardEvent) => {
			if (event.key === 'ArrowLeft') {
				event.preventDefault();
				goPrev();
			} else if (event.key === 'ArrowRight') {
				event.preventDefault();
				goNext();
			}
		};
		window.addEventListener('keydown', handler);
		return () => window.removeEventListener('keydown', handler);
	}, [isCarousel, isViewportHovered, goPrev, goNext]);

	const goPrevRef = useRef(goPrev);
	const goNextRef = useRef(goNext);
	goPrevRef.current = goPrev;
	goNextRef.current = goNext;

	useEffect(() => {
		if (!isCarousel) return;
		const viewport = viewportRef.current;
		if (!viewport) return;
		const COOLDOWN_MS = 700;
		const MIN_DELTA = 4;
		let cooldownUntil = 0;
		const handler = (event: WheelEvent) => {
			if (Math.abs(event.deltaX) < Math.abs(event.deltaY)) return;
			event.preventDefault();
			if (Math.abs(event.deltaX) < MIN_DELTA) return;
			const now = performance.now();
			if (now < cooldownUntil) return;
			if (event.deltaX > 0) goNextRef.current();
			else goPrevRef.current();
			cooldownUntil = now + COOLDOWN_MS;
		};
		viewport.addEventListener('wheel', handler, { passive: false });
		return () => {
			viewport.removeEventListener('wheel', handler);
		};
	}, [isCarousel]);

	const centerActiveSlide = useCallback(
		(behavior: ScrollBehavior) => {
			const viewport = viewportRef.current;
			const slide = slideRefs.current[safeSlide];
			if (!viewport || !slide) return;
			const target =
				slide.offsetLeft - (viewport.clientWidth - slide.clientWidth) / 2;
			viewport.scrollTo({
				left: Math.max(0, target),
				behavior,
			});
		},
		[safeSlide],
	);

	useLayoutEffect(() => {
		centerActiveSlide(hasPositionedRef.current ? 'smooth' : 'auto');
		hasPositionedRef.current = true;
	}, [centerActiveSlide]);

	useEffect(() => {
		const slide = slideRefs.current[safeSlide];
		if (!slide) return;
		let firstCall = true;
		const observer = new ResizeObserver(() => {
			if (firstCall) {
				firstCall = false;
				return;
			}
			centerActiveSlide('auto');
		});
		observer.observe(slide);
		return () => observer.disconnect();
	}, [safeSlide, centerActiveSlide]);

	if (!selectedPost) return null;

	return (
		<div className={cn('flex min-h-0 flex-1 items-start gap-4', className)}>
			{posts.length > 1 && (
				<div
					className="flex h-full min-h-0 min-w-[70px] flex-col gap-2
						overflow-x-hidden overflow-y-auto"
				>
					{posts.map((post, index) => {
						const postSlides = post.content.image_urls ?? [];
						const isActivePost = selectedPostIndex === index;
						const badgeIndex = isActivePost ? safeSlide + 1 : 1;
						return (
							<div
								key={post.id}
								className={cn(
									'relative cursor-pointer transition-all',
									'm-0.5 h-16 w-16 flex-shrink-0',
								)}
							>
								<BlobImage
									src={postSlides[0]}
									alt={`Post ${index + 1}`}
									className={cn(
										'box-border h-full w-full rounded-lg object-cover',
										!isActivePost
											? 'opacity-60 hover:opacity-100'
											: 'border-2 border-white/90',
									)}
									onClick={() => onSelectPost?.(index)}
								/>
								{postSlides.length > 1 && (
									<div
										className="pointer-events-none absolute right-0.5 bottom-0.5
											rounded-full bg-black/60 px-1 py-0.5 text-[10px]
											font-medium text-white"
									>
										{badgeIndex}/{postSlides.length}
									</div>
								)}
							</div>
						);
					})}
				</div>
			)}

			<div className="flex flex-[7] flex-col">
				<div
					ref={viewportRef}
					className="[container-type:inline-size] relative aspect-square
						max-h-[80vh] min-h-[300px] w-full overflow-x-hidden
						overscroll-x-contain scroll-smooth rounded-2xl"
					onMouseEnter={() => setIsViewportHovered(true)}
					onMouseLeave={() => setIsViewportHovered(false)}
				>
					<div className="flex h-full items-center gap-3">
						<div
							aria-hidden
							className="h-full flex-shrink-0"
							style={{ width: '50cqw' }}
						/>
						{slides.map((url, i) => {
							const isActive = i === safeSlide;
							return (
								<div
									key={`${i}-${url}`}
									ref={(el) => {
										slideRefs.current[i] = el;
									}}
									onClick={isActive ? undefined : () => setCurrentSlide(i)}
									className={cn(
										`relative h-full flex-shrink-0 transition-opacity
										duration-300`,
										isActive
											? 'opacity-100'
											: 'cursor-pointer opacity-30 hover:opacity-50',
									)}
								>
									<BlobImage
										src={url}
										alt={
											isCarousel
												? `Post preview slide ${i + 1} of ${slides.length}`
												: 'Post preview'
										}
										className="block h-full w-auto max-w-[88cqw] rounded-2xl
											object-cover"
									/>

									{isActive && (
										<div
											className={cn(
												`absolute top-4 left-4 z-10 flex flex-col gap-2
												transition-opacity duration-150`,
												// The download menu portals outside the viewport, so
												// moving the pointer into it un-hovers the viewport;
												// keep the rail visible while the menu is open.
												isViewportHovered || isDownloadMenuOpen
													? 'opacity-100'
													: 'pointer-events-none opacity-0',
											)}
										>
											<PostDownloadButton
												imageUrls={slides}
												artifactId={selectedPost.id}
												activeSlideIndex={safeSlide}
												onMenuOpenChange={setIsDownloadMenuOpen}
											/>
										</div>
									)}
								</div>
							);
						})}
						<div
							aria-hidden
							className="h-full flex-shrink-0"
							style={{ width: '50cqw' }}
						/>
					</div>
				</div>

				{isCarousel && (
					<div
						className="flex justify-center gap-2 px-4 pt-2"
						role="tablist"
						aria-label="Carousel slides"
					>
						{slides.map((_, i) => (
							<button
								key={i}
								type="button"
								role="tab"
								aria-selected={i === safeSlide}
								aria-label={`Go to slide ${i + 1}`}
								onClick={() => setCurrentSlide(i)}
								className={cn(
									'size-2 rounded-full transition-colors',
									i === safeSlide
										? 'bg-(--text-primary)'
										: 'bg-(--text-muted)/40 hover:bg-(--text-muted)/70',
								)}
							/>
						))}
					</div>
				)}
			</div>

			<div className="flex min-h-0 flex-[4] flex-col self-stretch">
				{contentPanel}
			</div>
		</div>
	);
};
